import { useEffect, useMemo, useState } from 'react';
import { CapTablesService } from 'api';
import { Fund } from 'common/types/fund/fund';
import { Logger } from 'utilities';
import { OwnershipOption } from './useOwnershipOptions.props';
import { CashFlowLedgerItem } from '../data/types';

export interface UseServerGrossIRRSummaryParams {
  captableId?: number | null;
  fundList: Fund[] | null | undefined;
  cashFlows?: CashFlowLedgerItem[];
  ownershipOptions?: OwnershipOption[];
  // Refresh signal: bump this whenever the underlying CFL data changes so
  // the IRR refetches. Avoids deep-equality on the cashFlows array.
  refreshKey?: unknown;
}

export interface UseServerGrossIRRSummaryReturn {
  fundsGrossIrr: Record<string, number | null>;
  firmGrossIrr: number | null;
  fundsInvestments: Record<string, number>;
  fundsProceeds: Record<string, number>;
  firmInvestments: number;
  firmProceeds: number;
  fundIdToName: Record<number, string>;
  isLoading: boolean;
  error: Error | null;
}

// Server-sourced replacement for the client-side `useGrossIRRSummary`. Calls
// the captable gross-irrs endpoint so the Cash Flow Summary table reads the
// same IRR computation that powers waterfall / fund summary / firm summary
// (single source of truth in `cap_table.services.gross_irr`). Investments /
// proceeds still come from the client-side CFL rows because they're trivial
// sums and the server endpoint focuses on IRR only.
export const useServerGrossIRRSummary = ({
  captableId,
  fundList,
  cashFlows = [],
  ownershipOptions = [],
  refreshKey,
}: UseServerGrossIRRSummaryParams): UseServerGrossIRRSummaryReturn => {
  const fundIdToName = useMemo(() => {
    const mapping: Record<number, string> = {};

    ownershipOptions.forEach(option => {
      if (option.type === 'fund' && option.value) {
        const match = option.value.match(/^fund-(\d+)$/);
        if (match) {
          const fundId = parseInt(match[1], 10);
          mapping[fundId] = option.label;
        }
      }
    });

    if (fundList && Array.isArray(fundList)) {
      fundList.forEach(fund => {
        if (typeof fund === 'undefined' || !fund.id || !fund.name) {
          return;
        }
        if (!mapping[fund.id]) {
          mapping[fund.id] = fund.name;
        }
      });
    }

    return mapping;
  }, [ownershipOptions, fundList]);

  const [fundsGrossIrr, setFundsGrossIrr] = useState<Record<string, number | null>>({});
  const [firmGrossIrr, setFirmGrossIrr] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!captableId) {
      return () => {};
    }
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    CapTablesService.apiCapTablesGrossIrrsRead(captableId)
      .then(response => {
        if (cancelled) {
          return;
        }
        // DRF DecimalField serializes as string; parse here so consumers
        // can stay number-typed (matches the client-side hook's contract).
        const parsedFunds: Record<string, number | null> = {};
        Object.entries(response.funds_gross_irr ?? {}).forEach(([fundId, value]) => {
          parsedFunds[fundId] = value === null || value === undefined ? null : Number(value);
        });
        setFundsGrossIrr(parsedFunds);
        setFirmGrossIrr(
          response.firm_gross_irr === null || response.firm_gross_irr === undefined
            ? null
            : Number(response.firm_gross_irr)
        );
      })
      .catch(err => {
        if (cancelled) {
          return;
        }
        Logger.error('Failed to fetch captable gross IRRs', err);
        setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [captableId, refreshKey]);

  // Investments / proceeds are simple positive/negative sums; computing them
  // client-side avoids a second endpoint and is independent of xirr math.
  const { fundsInvestments, fundsProceeds, firmInvestments, firmProceeds } = useMemo(() => {
    const investmentsByFund: Record<string, number> = {};
    const proceedsByFund: Record<string, number> = {};
    let firmInv = 0;
    let firmPro = 0;

    cashFlows.forEach(item => {
      if (item.is_excluded_from_calculations) {
        return;
      }
      let fundId: number | null = null;
      if (item.owner_fund) {
        fundId = item.owner_fund;
      } else if (typeof item.owner === 'string' && item.owner.startsWith('fund-')) {
        const match = item.owner.match(/^fund-(\d+)$/);
        if (match) {
          fundId = parseInt(match[1], 10);
        }
      }
      if (fundId === null) {
        return;
      }
      const signed = Number(item.amount) || 0;
      if (signed === 0) {
        return;
      }
      if (signed > 0) {
        proceedsByFund[fundId] = (proceedsByFund[fundId] ?? 0) + signed;
        firmPro += signed;
      } else {
        investmentsByFund[fundId] = (investmentsByFund[fundId] ?? 0) + signed;
        firmInv += signed;
      }
    });

    return {
      fundsInvestments: investmentsByFund,
      fundsProceeds: proceedsByFund,
      firmInvestments: firmInv,
      firmProceeds: firmPro,
    };
  }, [cashFlows]);

  return {
    fundsGrossIrr,
    firmGrossIrr,
    fundsInvestments,
    fundsProceeds,
    firmInvestments,
    firmProceeds,
    fundIdToName,
    isLoading,
    error,
  };
};
